import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  NetworkCheck,
  Refresh,
  Info,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from "@mui/icons-material";

const NetworkTroubleshooter = ({ show = true, onRetry, errorMessage }) => {
  const [expanded, setExpanded] = useState(false);
  const [testResults, setTestResults] = useState({});

  // Don't render if show is false
  if (!show) {
    return null;
  }

  const troubleshootingSteps = [
    {
      id: "connection",
      title: "Check Internet Connection",
      description: "Verify your device is connected to the internet",
      icon: <NetworkCheck />,
      action: () => testNetworkConnection(),
    },
    {
      id: "refresh",
      title: "Clear Browser Cache",
      description: "Press Ctrl+F5 (or Cmd+Shift+R on Mac) to hard refresh",
      icon: <Refresh />,
      action: () => window.location.reload(true),
    },
    {
      id: "wait",
      title: "Wait and Retry",
      description:
        "If you just logged out, wait 10-15 seconds before trying again",
      icon: <Info />,
      action: null,
    },
  ];

  const testNetworkConnection = async () => {
    try {
      setTestResults((prev) => ({ ...prev, connection: "testing" }));

      // Test connection to a reliable endpoint
      await fetch("https://httpbin.org/status/200", {
        method: "GET",
        mode: "no-cors",
        cache: "no-cache",
      });

      setTestResults((prev) => ({ ...prev, connection: "success" }));
    } catch (error) {
      setTestResults((prev) => ({ ...prev, connection: "failed" }));
    }
  };

  const getStatusIcon = (stepId) => {
    const status = testResults[stepId];
    switch (status) {
      case "testing":
        return <Refresh sx={{ animation: "spin 1s linear infinite" }} />;
      case "success":
        return <CheckCircle color="success" />;
      case "failed":
        return <ErrorIcon color="error" />;
      default:
        return <Warning color="warning" />;
    }
  };

  return (
    <Card sx={{ mt: 2, border: "1px solid", borderColor: "warning.main" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <NetworkCheck color="warning" />
            <Typography variant="h6" color="warning.main">
              Network Connection Issues
            </Typography>
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          Having trouble connecting? Try these troubleshooting steps:
        </Alert>

        <Collapse in={expanded}>
          <List>
            {troubleshootingSteps.map((step) => (
              <ListItem
                key={step.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemIcon>
                  {testResults[step.id] ? getStatusIcon(step.id) : step.icon}
                </ListItemIcon>
                <ListItemText
                  primary={step.title}
                  secondary={step.description}
                />
                {step.action && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={step.action}
                    disabled={testResults[step.id] === "testing"}
                  >
                    {step.id === "connection" ? "Test" : "Try"}
                  </Button>
                )}
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onRetry}
              startIcon={<Refresh />}
            >
              Retry Login
            </Button>

            <Chip
              label="💡 Tip: Network issues often resolve after a brief wait"
              variant="outlined"
              size="small"
            />
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Error Details:</strong> {errorMessage}
              </Typography>
            </Alert>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default NetworkTroubleshooter;
